import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AssignmentSummary,
  ProviderModelsResponse,
  ProviderSummary,
  SettingsStatusResponse,
  type AssignmentUpsertRequest,
  type ProviderCreateRequest,
} from "@oracus/shared/api/settings";

import { apiClient } from "./client.ts";

export function useSettingsStatus() {
  return useQuery({
    queryKey: ["settings", "status"],
    queryFn: () =>
      apiClient.get("/api/settings/status", SettingsStatusResponse),
  });
}

export function useCreateProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: ProviderCreateRequest) =>
      apiClient.post("/api/settings/providers", req, ProviderSummary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useDeleteProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete(`/api/settings/providers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useUpsertAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      task,
      ...req
    }: AssignmentUpsertRequest & { task: string }) =>
      apiClient.put(
        `/api/settings/assignments/${encodeURIComponent(task)}`,
        req,
        AssignmentSummary,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useClearAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (task: string) =>
      apiClient.delete(`/api/settings/assignments/${encodeURIComponent(task)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useProviderModels(providerId: string) {
  return useQuery({
    queryKey: ["settings", "providers", providerId, "models"],
    queryFn: () =>
      apiClient.get(
        `/api/settings/providers/${providerId}/models`,
        ProviderModelsResponse,
      ),
    enabled: false,
    retry: false,
    staleTime: 30_000,
  });
}
